// Setups step — machine-setup list for machined parts.
//
// Per spec/14, v1 had nested operations under each machine. For v2 we keep
// the wizard layer simpler: machine + fixture + setup hrs + run min/piece.
// The richer nested-op editor is a future pass.
//
// MachineSetup in crmTypes is:
//   { machine: string, hrsPerTier: number[], setupHrs: number }
// The "run min/piece" input here maps to hrsPerTier[0] (we store hours per
// the schema; run min / 60 → hours, single-tier default). Multi-tier
// override comes in the quote builder.
// Fixture is captured into the .machine string with a " · " separator —
// future pass adds a dedicated fixture field.

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { MachineSetup } from '@/shared/crmTypes';

interface SetupRow {
  machine: string;
  fixture: string;
  setupHrs: string;
  runMinPerPiece: string;
}

const COMMON_MACHINES = [
  'Doosan DNM 750',
  'Haas GR-712',
  'Mazak Integrex',
  'Doosan NHM-6300',
  'Manual mill',
  'Lathe',
  'Other',
];

function parseRow(row: MachineSetup): SetupRow {
  const [machine, ...fixtureParts] = (row.machine ?? '').split(' · ');
  const fixture = fixtureParts.join(' · ');
  const runMinPerPiece = ((row.hrsPerTier?.[0] ?? 0) * 60).toString();
  return {
    machine: machine || '',
    fixture,
    setupHrs: row.setupHrs?.toString() ?? '',
    runMinPerPiece: runMinPerPiece === '0' ? '' : runMinPerPiece,
  };
}

function serializeRow(r: SetupRow): MachineSetup {
  const runHrs = Number.parseFloat(r.runMinPerPiece || '0') / 60;
  const setupHrs = Number.parseFloat(r.setupHrs || '0');
  return {
    machine: r.fixture ? `${r.machine} · ${r.fixture}` : r.machine,
    hrsPerTier: [Number.isFinite(runHrs) ? runHrs : 0],
    setupHrs: Number.isFinite(setupHrs) ? setupHrs : 0,
  };
}

export function SetupsStep({ part, onChange }: StepProps) {
  const rows: SetupRow[] = (part.machines ?? []).map(parseRow);

  function updateRow(i: number, patch: Partial<SetupRow>) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ machines: next.map(serializeRow) });
  }

  function addRow() {
    const next = [
      ...rows,
      { machine: 'Doosan DNM 750', fixture: '', setupHrs: '', runMinPerPiece: '' },
    ];
    onChange({ machines: next.map(serializeRow) });
  }

  function removeRow(i: number) {
    const next = rows.filter((_, idx) => idx !== i);
    onChange({ machines: next.map(serializeRow) });
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="text-sm text-text-mute italic border border-dashed border-line-dark rounded p-4 text-center">
          No machines yet. Add the machines + fixtures this part runs on.
        </div>
      )}

      {rows.map((r, i) => (
        <div
          key={i}
          className="border border-line rounded p-3 bg-bg/30 space-y-2"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={`Machine #${i + 1}`}>
              <select
                value={r.machine}
                onChange={(e) => updateRow(i, { machine: e.target.value })}
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
              >
                <option value="">— pick machine —</option>
                {COMMON_MACHINES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Fixture">
              <input
                type="text"
                value={r.fixture}
                onChange={(e) => updateRow(i, { fixture: e.target.value })}
                placeholder='e.g. Kurt 6" vise'
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            <Field label="Setup (hrs)">
              <NumberInput
                value={r.setupHrs === '' ? undefined : Number.parseFloat(r.setupHrs)}
                onChange={(v) => updateRow(i, { setupHrs: v === undefined ? '' : String(v) })}
                step={0.5}
                unit="hrs"
                placeholder="0.5"
                numpadTitle="Setup hours"
              />
            </Field>
            <Field label="Run min/piece">
              <NumberInput
                value={r.runMinPerPiece === '' ? undefined : Number.parseFloat(r.runMinPerPiece)}
                onChange={(v) => updateRow(i, { runMinPerPiece: v === undefined ? '' : String(v) })}
                step={0.5}
                unit="min"
                placeholder="12"
                numpadTitle="Run minutes / piece"
              />
            </Field>
            <div className="text-right">
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-xs text-text-mute hover:text-orange py-1.5 px-2"
              >
                ✕ Remove
              </button>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="w-full text-sm text-orange border border-dashed border-orange/40 rounded py-2 hover:bg-orange/5 font-semibold"
      >
        + Add machine
      </button>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
